import type { ComponentProps, ReactNode } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/cn';

type Props = {
  src?: string;
  alt: string;
  size?: 12 | 16 | 24 | 32;
  fallback: ReactNode;
  hoverScale?: boolean;
  className?: string;
} & ComponentProps<typeof Avatar>;

// サイズに基づくイニシャルフォントサイズ設定
const fontSize = {
  12: 'text-[8px]',
  16: 'text-[10px]',
  24: 'text-xs',
  32: 'text-sm',
} satisfies Record<NonNullable<Props['size']>, string>;

/**
 * アバターの基底コンポーネントです。
 * アバターのサイズを指定することができます。
 * アバターのサイズは、12px, 16px, 24px, 32pxの4種類が指定できます。
 *
 * AvatarApp, AvatarDevice, AvatarPersonなどで利用されています。
 * 上記の具象コンポーネントはfeatureディレクトリに配置されています。
 * （本当は基底コンポーネントだから上記を知るのも良くないのだけれど、辞書的役割を担っておきたいため記述しています。）
 */
export function AvatarBase(props: Props) {
  const {
    src,
    alt,
    size = 24,
    shape = 'circle',
    fallback,
    hoverScale = false,
    className,
    ...rest
  } = props;

  return (
    <Avatar
      shape={shape}
      className={cn(
        hoverScale && 'transition-transform hover:scale-125',
        `h-${size / 4} w-${size / 4}`,
        fontSize[size],
        className,
      )}
      {...rest}
    >
      <AvatarImage src={src} alt={alt} />
      <AvatarFallback className="m-0">{fallback}</AvatarFallback>
    </Avatar>
  );
}
